import { useState, useRef } from 'react';
import { SpeechRecognition, SpeechRecognitionConstructor } from '../typings/speech';

export const useSpeechRecognition = (onResult: (text: string) => void) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const initializeRecognition = () => {
    type ExtendedWindow = Window & {
      SpeechRecognition?: SpeechRecognitionConstructor;
      webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };

    const win = window as ExtendedWindow;

    const SpeechRecognitionClass =
      win.SpeechRecognition || win.webkitSpeechRecognition;

    if (!SpeechRecognitionClass) {
      alert('Speech recognition is not supported in this browser.');
      return null;
    }

    if (!recognitionRef.current) {
      const recognition = new SpeechRecognitionClass();
      recognition.lang = 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        onResult(transcript);
      };

      recognition.onerror = (event: any) => {
        if (event.error === 'no-speech') {
          console.warn('No speech detected, please try again.');
        } else {
          console.error('Speech recognition error:', event.error);
        }
        setListening(false);
      };

      recognition.onend = () => {
        setListening(false); // auto-stop updates UI
      };

      recognitionRef.current = recognition;
    }

    return recognitionRef.current;
  };

  const startSpeechRecognition = () => {
    const recognition = initializeRecognition();
    if (recognition && !listening) {
      recognition.start();
      setListening(true);
    }
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current && listening) {
      recognitionRef.current.stop();
      setListening(false);
    }
  };

  return { listening, startSpeechRecognition, stopSpeechRecognition };
};
